import { SessionLeafNode } from '@biconomy/modules/dist/src/interfaces/ISessionStorage'
import { Period } from '@kwenta/sdk/constants'
import {
	FuturesMarketAsset,
	FuturesMarketKey,
	FuturesPositionHistory,
	SwapDepositToken,
	TransactionStatus,
} from '@kwenta/sdk/types'
import Wei from '@synthetixio/wei'

import { PricesInfo } from 'state/prices/types'
import { QueryStatus } from 'state/types'

import { AppFuturesMarginType, FuturesTransactionType } from './common/types'

export type MarkPrices<T = Wei> = Partial<Record<FuturesMarketKey, T>>

export type MarkPriceInfos<T = Wei> = Partial<Record<FuturesMarketKey, PricesInfo<T>>>

export type FundingRate<T = Wei> = {
	asset: FuturesMarketKey
	fundingTitle: string
	fundingRate: T | null
}

export type FuturesAction = {
	account: string
	timestamp: number
	asset: FuturesMarketAsset
	margin: number
	size: number
	action: 'trade' | 'deposit' | 'withdraw'
}

export type PortfolioValues = {
	timestamp: number
	total: number
}

export type FuturesTransaction = {
	type: FuturesTransactionType
	status: TransactionStatus
	error?: string
	hash: string | null
}

type FuturesNetwork = number

export type InputCurrencyDenomination = 'usd' | 'native'

export type FundingRatePeriods = {
	[key: number]: string
}

export enum TradingModes {
	DEFAULT = 'DEFAULT',
	ONE_CLICK = 'ONE_CLICK',
}

export enum ManageModalType {
	Deposit = 0,
	Withdraw = 1,
	Bridge = 2,
	OneClick = 3,
	Delegation = 4,
}

export type FuturesState = {
	selectedType: AppFuturesMarginType
	confirmationModalOpen: boolean
	selectedManageModalType: ManageModalType
	selectedInputDenomination: InputCurrencyDenomination
	selectedInputHours: number
	selectedChart: 'price' | 'funding'
	historicalFundingRatePeriod: Period
	selectedSwapDepositToken: SwapDepositToken
	preferences: {
		showHistory?: boolean
		tradingMode: TradingModes
	}
	oneClickTrading: {
		accountDelegated: boolean
		accountBalance: string
		sessionInfo?: SessionLeafNode
	}
	dashboard: {
		selectedPortfolioTimeframe: Period
	}
	leaderboard: {
		selectedTrader?: { trader: string; traderEns?: string | null }
		selectedTraderPositionHistory: Record<
			FuturesNetwork,
			{
				[wallet: string]: FuturesPositionHistory<string>[]
			}
		>
	}
	tradePanelDrawerOpen: boolean
	queryStatuses: {
		selectedTraderPositionHistory: QueryStatus
		selectedTraderDelegatesList: QueryStatus
		selectedTraderSubAccountsList: QueryStatus
	}
	delegation: {
		selectedDelegationAddress: string
		selectedSubAccountAddress: string
		newNickname: string
		delegatesList: Array<DelegationAccountInfo>
		subAccountList: Array<DelegationAccountInfo>
		addressBook: Record<string, Array<DelegationAccountInfo>>
	}
}

export type ExecuteDelayedOrderInputs = {
	marketKey: FuturesMarketKey
	marketAddress: string
}

export type ExecuteAsyncOrderInputs = {
	marketKey: FuturesMarketKey
	marketId: number
}

export const futuresPositionKeys = new Set([
	'remainingMargin',
	'accessibleMargin',
	'order.fee',
	'order.leverage',
	'position.notionalValue',
	'position.accruedFunding',
	'position.initialMargin',
	'position.profitLoss',
	'position.lastPrice',
	'position.size',
	'position.liquidationPrice',
	'position.initialLeverage',
	'position.leverage',
	'position.pnl',
	'position.pnlPct',
	'position.marginRatio',
])

export type DelegationAccountInfo = {
	address: string
	nickname: string
}
